import { FunctionComponent, memo, useCallback, useContext, useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { ChannelContext, EventDetailsModalContext } from '../../../../context';
import { getEventModal } from '../../../../slices';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { Modal } from '../../Modal';
import { EventDetailsCommon } from '../EventDetailsCommon';

const EVENT_DETAILS_MODAL_CONTENT_ID = 'event-details-modal-content';

export const EventDetailsModal: FunctionComponent = memo(() => {
  const dispatch = useAppDispatch();

  const { eventDetailsModalData, setEventDetailsModalData } = useContext(EventDetailsModalContext);

  const { eventId, initialTab } = eventDetailsModalData;

  const { id: channelId } = useContext(ChannelContext).channel;

  const { event, isFetched } = useAppSelector(({ eventDetailsModal }) => eventDetailsModal);

  useEffect(() => {
    if (!eventId) {
      return;
    }

    dispatch(getEventModal({ channelId, eventId }));
  }, [channelId, dispatch, eventId]);

  const closeModal = useCallback(() => {
    setEventDetailsModalData({ eventId: null });
  }, [setEventDetailsModalData]);

  useEffect(() => {
    if (!isFetched || !event) {
      return;
    }

    if (!event.channels.find(({ id }) => id === channelId)) {
      closeModal();
    }
  }, [channelId, closeModal, event, isFetched]);

  const body = useMemo(() => {
    if (!isFetched || !event) {
      return <Skeleton width={'100%'} height={200} />;
    }

    return (
      <EventDetailsCommon
        event={event}
        onDeleteSuccess={closeModal}
        initialTab={initialTab}
        scrollableTarget={EVENT_DETAILS_MODAL_CONTENT_ID}
        noPadding
      />
    );
  }, [closeModal, event, initialTab, isFetched]);

  return (
    <Modal
      isOpen={Boolean(eventId)}
      body={body}
      onClose={closeModal}
      contentId={EVENT_DETAILS_MODAL_CONTENT_ID}
      ariaLabel={event?.name}
    />
  );
});
